/**
 * Tenant Domain Seed Data
 *
 * Contains all seed data for the Tenant Service (25):
 * - Tenants
 * - Realms
 * - RealmTemplates
 * - Businesses
 */

import { seedId } from '../utils';

// =============================================================================
// TENANT IDs
// =============================================================================

export const TENANT_IDS = {
  /** Platform tenant - owns platform administration realm */
  PLATFORM: seedId('tenant-platform', 1),
  /** Local development tenant - owns demo business realms */
  LOCAL_DEV: seedId('tenant-local-dev', 2),
} as const;

export type TenantId = (typeof TENANT_IDS)[keyof typeof TENANT_IDS];

// =============================================================================
// REALM TEMPLATE IDs
// =============================================================================

export const REALM_TEMPLATE_IDS = {
  /** Default blank template */
  DEFAULT: seedId('template-default', 1),
  /** Blank template with no pre-configuration */
  BLANK: seedId('template-blank', 2),
  /** Loan Origination System template */
  LOS: seedId('template-los', 3),
  /** CRM template */
  CRM: seedId('template-crm', 4),
  /** Manufacturing template */
  MFG: seedId('template-mfg', 5),
} as const;

export type RealmTemplateId = (typeof REALM_TEMPLATE_IDS)[keyof typeof REALM_TEMPLATE_IDS];

// =============================================================================
// REALM IDs
// =============================================================================

export const REALM_IDS = {
  /** Platform default realm - for platform admins */
  PLATFORM_DEFAULT: seedId('realm-platform', 1),
  /** Loan servicing realm - main demo environment */
  LOAN_SERVICING: seedId('realm-loan-svc', 2),
} as const;

export type RealmId = (typeof REALM_IDS)[keyof typeof REALM_IDS];

// =============================================================================
// BUSINESS IDs
// =============================================================================

export const BUSINESS_IDS = {
  /** Platform administration business */
  PLATFORM_ADMIN: seedId('business-platform', 1),
  /** OpsMatrix Capital - primary demo company */
  OPSMATRIX_CAPITAL: seedId('business-opsmatrix', 2),
  /** 1Sharpe Capital - secondary demo company */
  ONE_SHARPE_CAPITAL: seedId('business-1sharpe', 3),
  /** Pacific Bridge Fund - small fund */
  PACIFIC_BRIDGE_FUND: seedId('business-pacific', 4),
  /** Gulf Coast Family Office - family office */
  GULF_COAST_FO: seedId('business-gulfcoast', 5),
  /** E2E Platform Business - canary/E2E testing (Issue #670) */
  E2E_PLATFORM: seedId('e2e-platform-business', 1),
} as const;

export type SeedBusinessId = (typeof BUSINESS_IDS)[keyof typeof BUSINESS_IDS];

// =============================================================================
// BUSINESS SLUGS (Single source of truth for slug strings used in seeders)
// =============================================================================

export const BUSINESS_SLUGS = {
  PLATFORM_ADMIN: 'platform-admin',
  OPSMATRIX_CAPITAL: 'fiops-capital',
  ONE_SHARPE_CAPITAL: '1sharpe-capital',
  PACIFIC_BRIDGE_FUND: 'pacific-bridge',
  GULF_COAST_FO: 'gulf-coast-fo',
} as const;

// =============================================================================
// TENANT DATA
// =============================================================================

export interface TenantSeedData {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  settings?: Record<string, unknown>;
  defaultBusinessId?: string;
}

export const TENANTS: Record<keyof typeof TENANT_IDS, TenantSeedData> = {
  PLATFORM: {
    id: TENANT_IDS.PLATFORM,
    name: 'Platform',
    slug: 'platform',
    domain: 'platform.opsmatrix.local',
    subscriptionTier: 'enterprise',
    subscriptionStatus: 'active',
    settings: { isPlatformTenant: true },
  },
  LOCAL_DEV: {
    id: TENANT_IDS.LOCAL_DEV,
    name: 'Local Development',
    slug: 'local-dev',
    subscriptionTier: 'enterprise',
    subscriptionStatus: 'active',
    defaultBusinessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
  },
};

// =============================================================================
// REALM TEMPLATE DATA
// =============================================================================

export interface RealmTemplateSeedData {
  id: string;
  code: string;
  name: string;
  description: string;
  isSystemTemplate: boolean;
  version: string;
  defaultFeatures: Record<string, boolean>;
  defaultServices: Record<string, boolean>;
}

export const REALM_TEMPLATES: Record<keyof typeof REALM_TEMPLATE_IDS, RealmTemplateSeedData> = {
  DEFAULT: {
    id: REALM_TEMPLATE_IDS.DEFAULT,
    code: 'default',
    name: 'Default Template',
    description: 'Default realm template with basic features',
    isSystemTemplate: true,
    version: '1.0.0',
    defaultFeatures: { dashboard: true, settings: true },
    defaultServices: { notifications: true, audit: true },
  },
  BLANK: {
    id: REALM_TEMPLATE_IDS.BLANK,
    code: 'blank',
    name: 'Blank Template',
    description: 'Empty template with no pre-configured features',
    isSystemTemplate: true,
    version: '1.0.0',
    defaultFeatures: {},
    defaultServices: {},
  },
  LOS: {
    id: REALM_TEMPLATE_IDS.LOS,
    code: 'los',
    name: 'Loan Origination System',
    description: 'Full-featured loan origination and servicing platform',
    isSystemTemplate: true,
    version: '1.0.0',
    defaultFeatures: {
      loanPipeline: true,
      underwriting: true,
      closing: true,
      servicing: true,
      drawManagement: true,
      consumerPortal: true,
      partnerPortal: true,
      documentManagement: true,
      reporting: true,
      aiAssistants: true,
    },
    defaultServices: {
      notifications: true,
      audit: true,
      billing: true,
      entityFramework: true,
      caseManagement: true,
      crm: true,
    },
  },
  CRM: {
    id: REALM_TEMPLATE_IDS.CRM,
    code: 'crm',
    name: 'CRM Template',
    description: 'Customer relationship management template',
    isSystemTemplate: true,
    version: '1.0.0',
    defaultFeatures: {
      leads: true,
      deals: true,
      contacts: true,
      pipeline: true,
      reporting: true,
    },
    defaultServices: { notifications: true, audit: true },
  },
  MFG: {
    id: REALM_TEMPLATE_IDS.MFG,
    code: 'mfg',
    name: 'Manufacturing Template',
    description: 'Manufacturing and production management template',
    isSystemTemplate: true,
    version: '1.0.0',
    defaultFeatures: {
      inventory: true,
      production: true,
      quality: true,
      maintenance: true,
    },
    defaultServices: { notifications: true, audit: true },
  },
};

// =============================================================================
// REALM DATA
// =============================================================================

export interface RealmSeedData {
  id: string;
  tenantId: string;
  templateId: string;
  name: string;
  slug: string;
  description: string;
  status: string;
}

export const REALMS: Record<keyof typeof REALM_IDS, RealmSeedData> = {
  PLATFORM_DEFAULT: {
    id: REALM_IDS.PLATFORM_DEFAULT,
    tenantId: TENANT_IDS.PLATFORM,
    templateId: REALM_TEMPLATE_IDS.DEFAULT,
    name: 'Platform Default',
    slug: 'platform-default',
    description: 'Default realm for platform administration',
    status: 'active',
  },
  LOAN_SERVICING: {
    id: REALM_IDS.LOAN_SERVICING,
    tenantId: TENANT_IDS.LOCAL_DEV,
    templateId: REALM_TEMPLATE_IDS.LOS,
    name: 'Coastal Funding',
    slug: 'coastal-funding',
    description: 'Primary loan servicing demo environment',
    status: 'active',
  },
};

// =============================================================================
// BUSINESS ADDRESS DATA
// =============================================================================

export interface BusinessAddressSeedData {
  street: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
}

// =============================================================================
// BUSINESS DATA
// =============================================================================

export interface BusinessSeedData {
  id: string;
  realmId: string;
  name: string;
  slug: string;
  domain?: string;
  description?: string;
  industry?: string;
  companySize?: string;
  // Enhanced fields
  legalName?: string;
  dbaName?: string;
  taxId?: string; // EIN
  address?: BusinessAddressSeedData;
  phone?: string;
  fax?: string;
  email?: string;
  website?: string;
  // Licensing
  nmlsId?: string;
  stateLicenses?: {
    state: string;
    licenseNumber: string;
    licenseType: 'CFL' | 'CRMLA' | 'DBO' | 'MLO' | 'OTHER';
    expirationDate?: string;
  }[];
  // Business details
  foundedYear?: number;
  aum?: number; // Assets Under Management
  loanVolume?: number; // Annual loan volume
  avgLoanSize?: number;
  loanProducts?: string[];
  targetMarkets?: string[];
  // Operations
  operatingHours?: string;
  timezone?: string;
  // B2B Capabilities (Issue #238)
  canLend?: boolean;
  canBroker?: boolean;
  canReceiveReferrals?: boolean;
}

export const BUSINESSES: Record<keyof typeof BUSINESS_IDS, BusinessSeedData> = {
  PLATFORM_ADMIN: {
    id: BUSINESS_IDS.PLATFORM_ADMIN,
    realmId: REALM_IDS.PLATFORM_DEFAULT,
    name: 'Platform Administration',
    slug: 'platform-admin',
    description: 'Platform administration business',
    industry: 'Technology',
    companySize: '10-50',
    legalName: 'Fiops Inc.',
    address: {
      street: '500 Congress Avenue',
      unit: 'Suite 1500',
      city: 'Austin',
      state: 'TX',
      zip: '78701',
      county: 'Travis',
    },
    phone: '+1-512-555-0100',
    email: 'admin@opsmatrix.com',
    website: 'https://opsmatrix.com',
    foundedYear: 2020,
    operatingHours: 'Mon-Fri 9am-5pm CT',
    timezone: 'America/Chicago',
  },
  OPSMATRIX_CAPITAL: {
    id: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    name: 'Fiops Capital',
    slug: 'fiops-capital',
    domain: 'opsmatrix.local',
    description: 'Primary demo lender - private money lending',
    industry: 'Financial Services',
    companySize: '50-200',
    legalName: 'Fiops Capital LLC',
    dbaName: 'Fiops Capital',
    taxId: '84-1234567',
    address: {
      street: '816 Congress Avenue',
      unit: 'Suite 1200',
      city: 'Austin',
      state: 'TX',
      zip: '78701',
      county: 'Travis',
    },
    phone: '+1-512-555-0001',
    fax: '+1-512-555-0002',
    email: 'loans@opsmatrixcapital.com',
    website: 'https://opsmatrixcapital.com',
    nmlsId: '1234567',
    stateLicenses: [
      { state: 'TX', licenseNumber: 'CFL-123456', licenseType: 'CFL', expirationDate: '2027-12-31' },
      { state: 'FL', licenseNumber: 'MLD-789012', licenseType: 'OTHER', expirationDate: '2027-06-30' },
      { state: 'CA', licenseNumber: 'DBO-345678', licenseType: 'DBO', expirationDate: '2027-09-30' },
    ],
    foundedYear: 2018,
    aum: 250000000,
    loanVolume: 180000000,
    avgLoanSize: 850000,
    loanProducts: ['Bridge', 'Fix & Flip', 'DSCR', 'Construction', 'Commercial'],
    targetMarkets: ['Texas', 'Florida', 'California', 'Arizona', 'Nevada'],
    operatingHours: 'Mon-Fri 8am-6pm CT',
    timezone: 'America/Chicago',
    canLend: true,
    canBroker: true,
    canReceiveReferrals: true,
  },
  ONE_SHARPE_CAPITAL: {
    id: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    name: '1Sharpe Capital',
    slug: '1sharpe-capital',
    domain: '1sharpe.local',
    description: 'Secondary demo lender - bridge and construction',
    industry: 'Financial Services',
    companySize: '10-50',
    legalName: '1Sharpe Capital Partners LLC',
    dbaName: '1Sharpe Capital',
    taxId: '84-2345678',
    address: {
      street: '1845 Woodall Rodgers Freeway',
      unit: 'Suite 800',
      city: 'Dallas',
      state: 'TX',
      zip: '75201',
      county: 'Dallas',
    },
    phone: '+1-214-555-0001',
    fax: '+1-214-555-0002',
    email: 'lending@1sharpe.com',
    website: 'https://1sharpe.com',
    nmlsId: '2345678',
    stateLicenses: [{ state: 'TX', licenseNumber: 'CFL-234567', licenseType: 'CFL', expirationDate: '2027-12-31' }],
    foundedYear: 2019,
    aum: 75000000,
    loanVolume: 65000000,
    avgLoanSize: 650000,
    loanProducts: ['Bridge', 'Fix & Flip', 'Ground-Up Construction'],
    targetMarkets: ['Texas', 'Oklahoma', 'Louisiana'],
    operatingHours: 'Mon-Fri 8am-5pm CT',
    timezone: 'America/Chicago',
    canLend: true,
    canBroker: true,
    canReceiveReferrals: true,
  },
  PACIFIC_BRIDGE_FUND: {
    id: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    realmId: REALM_IDS.LOAN_SERVICING,
    name: 'Pacific Bridge Fund',
    slug: 'pacific-bridge',
    domain: 'pacificbridge.local',
    description: 'Regional bridge lending fund',
    industry: 'Financial Services',
    companySize: '1-10',
    legalName: 'Pacific Bridge Fund LP',
    dbaName: 'Pacific Bridge',
    taxId: '84-3456789',
    address: {
      street: '600 California Street',
      unit: 'Suite 1800',
      city: 'San Francisco',
      state: 'CA',
      zip: '94108',
      county: 'San Francisco',
    },
    phone: '+1-415-555-0001',
    email: 'info@pacificbridgefund.com',
    website: 'https://pacificbridgefund.com',
    nmlsId: '3456789',
    stateLicenses: [{ state: 'CA', licenseNumber: 'CRMLA-456789', licenseType: 'CRMLA', expirationDate: '2027-12-31' }],
    foundedYear: 2021,
    aum: 35000000,
    loanVolume: 28000000,
    avgLoanSize: 1200000,
    loanProducts: ['Bridge', 'DSCR'],
    targetMarkets: ['California', 'Nevada', 'Oregon', 'Washington'],
    operatingHours: 'Mon-Fri 9am-5pm PT',
    timezone: 'America/Los_Angeles',
  },
  GULF_COAST_FO: {
    id: BUSINESS_IDS.GULF_COAST_FO,
    realmId: REALM_IDS.LOAN_SERVICING,
    name: 'Gulf Coast Family Office',
    slug: 'gulf-coast-fo',
    domain: 'gulfcoastfo.local',
    description: 'Family office with real estate focus',
    industry: 'Financial Services',
    companySize: '1-10',
    legalName: 'Gulf Coast Family Office LLC',
    dbaName: 'GCFO Investments',
    taxId: '84-4567890',
    address: {
      street: '2800 Post Oak Boulevard',
      unit: 'Suite 4100',
      city: 'Houston',
      state: 'TX',
      zip: '77056',
      county: 'Harris',
    },
    phone: '+1-713-555-0001',
    email: 'investments@gcfo.com',
    website: 'https://gulfcoastfo.com',
    foundedYear: 2015,
    aum: 125000000,
    loanVolume: 45000000,
    avgLoanSize: 2500000,
    loanProducts: ['Bridge', 'Commercial', 'Mezzanine'],
    targetMarkets: ['Texas', 'Louisiana', 'Alabama', 'Mississippi'],
    operatingHours: 'Mon-Fri 9am-5pm CT',
    timezone: 'America/Chicago',
  },
  E2E_PLATFORM: {
    id: BUSINESS_IDS.E2E_PLATFORM,
    realmId: REALM_IDS.PLATFORM_DEFAULT,
    name: 'E2E Platform Business',
    slug: 'e2e-platform-business',
    domain: 'e2e-business.platform.local',
    description: 'E2E testing business for canary scenarios (Issue #670)',
    industry: 'Technology',
    companySize: '10-50',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all tenants as an array for seeding
 */
export function getAllTenants(): TenantSeedData[] {
  return Object.values(TENANTS);
}

/**
 * Get all realm templates as an array for seeding
 */
export function getAllRealmTemplates(): RealmTemplateSeedData[] {
  return Object.values(REALM_TEMPLATES);
}

/**
 * Get all realms as an array for seeding
 */
export function getAllRealms(): RealmSeedData[] {
  return Object.values(REALMS);
}

/**
 * Get all businesses as an array for seeding
 */
export function getAllBusinesses(): BusinessSeedData[] {
  return Object.values(BUSINESSES);
}

/**
 * Get businesses for a specific realm
 */
export function getBusinessesByRealm(realmId: string): BusinessSeedData[] {
  return Object.values(BUSINESSES).filter((b) => b.realmId === realmId);
}
